import type { Activity, BbchStageBase } from 'farmdok-rest-api';
import Handsontable from 'handsontable';

import { formatBbchCode, sanitizeBbchCode } from '@/activities/utils/bbchCode';
import {
  filterBbchStages,
  getProductIdsForPesticideIndicationFilter,
  getSelectedPesticideIndications,
} from '@/activities/utils/bbchFiltering';
import { getCachedBbchStages, loadBbchStages } from '@/activities/utils/bbchStages';
import type { Api } from '@/plugins/farmdokRestApi';
import { Product } from '@/shared/api/rest/models';
import type { DropdownItem, DropdownItemsPerCategory } from '@/shared/components/form/formFieldDropdownTypes';
import { columnSorting as columnSortingDropdown } from '@/shared/handsontable/rework/cellTypes/dropdown';
import { ColumnSettingsDropdownOptional } from '@/shared/handsontable/rework/cellTypes/dropdown/types';
import isFirstRowOfEntry from '@/shared/handsontable/rework/features/nestedTable/utils/isFirstRowOfEntry';
import store from '@/store';
import type { RootState } from '@/store/types';

type CropWithBbchGroup = Product & {
  bbchGroupId?: string | null;
};

type StoreWithApi = typeof store & { $api?: Api };

type BbchDropdownStage = {
  bbchCode: number;
  description: string;
  macroStage: number;
  macroStageName: string;
};

let loadBbchStagesForTooltipPromise: Promise<void> | null = null;
const pesticideIndicationsLoadingPromisesByProductId = new Map<string, Promise<void>>();

const fallbackDropdownItems: DropdownItemsPerCategory[] = [
  {
    id: 'bbch-fallback',
    items: Array.from({ length: 100 }, (_, bbchCode) => ({
      id: formatBbchCode(bbchCode),
      name: formatBbchCode(bbchCode),
    })),
    sort: false,
  },
];

const renderEmpty = (visualRow: number, instance: Handsontable) => !isFirstRowOfEntry(visualRow, instance);

function formatBbchMacroStageHeader(macroStage: number, macroStageName: string): string {
  const macroStageCode = `${Math.floor(macroStage)}X`;
  if (!macroStageName) {
    return `BBCH ${macroStageCode}`;
  }

  return `BBCH ${macroStageCode} - ${macroStageName}`;
}

function toBbchDropdownStage(bbchStage: BbchStageBase): BbchDropdownStage | null {
  const bbchCode = sanitizeBbchCode(bbchStage.bbchCode);
  if (bbchCode === null) {
    return null;
  }

  // bbchStageList currently returns these fields as always present.
  return {
    bbchCode,
    description: bbchStage.description?.trim() ?? '',
    macroStage: Math.floor(bbchStage.macroStage ?? 0),
    macroStageName: bbchStage.macroStageName?.trim() ?? '',
  };
}

function findCropById(cropId: string): CropWithBbchGroup | null {
  const crops = store.state.products.crops.data as Record<string, CropWithBbchGroup>;
  if (crops[cropId]) {
    return crops[cropId];
  }

  const crop = Object.values(crops).find((candidate) => String(candidate.id) === String(cropId));
  return crop ?? null;
}

function getBbchGroupIdForVisualRow(visualRow: number, hot: Handsontable): string | null {
  const fieldId = hot.getDataAtRowProp(visualRow, 'fieldDropdownItem')?.id;
  if (!fieldId) {
    return null;
  }

  const field = store.state.fields.data[fieldId];
  if (!field?.cropId) {
    return null;
  }

  return findCropById(field.cropId)?.bbchGroupId ?? null;
}

function getBbchGroupIdsForVisualRow(visualRow: number, hot: Handsontable): string[] {
  const bbchGroupId = getBbchGroupIdForVisualRow(visualRow, hot);
  if (!bbchGroupId) {
    return [];
  }

  return [bbchGroupId];
}

function findActivityById(activityId: string): Activity | null {
  const rootState = store.state as RootState;

  const activityFromActivitiesTable = rootState.activities.data[activityId];
  if (activityFromActivitiesTable) {
    return activityFromActivitiesTable;
  }

  const { activities } = rootState.activities.createEditActivity;
  return activities.find((activity) => activity.id === activityId) ?? null;
}

function getActivityForVisualRow(visualRow: number, hot: Handsontable): Activity | null {
  const activityId = hot.getDataAtRowProp(visualRow, 'id');
  if (!activityId) {
    return null;
  }

  return findActivityById(activityId);
}

function getProductIdsForPesticideIndicationFilterForVisualRow(visualRow: number, hot: Handsontable): string[] {
  const activity = getActivityForVisualRow(visualRow, hot);
  if (!activity) {
    return [];
  }

  return getProductIdsForPesticideIndicationFilter([activity]);
}

function getSelectedPesticideIndicationsForVisualRow(visualRow: number, hot: Handsontable) {
  const activity = getActivityForVisualRow(visualRow, hot);
  if (!activity) {
    return [];
  }

  const pesticideIndicationsByProduct = (store.state as RootState).pesticideIndications.data;
  return getSelectedPesticideIndications([activity], pesticideIndicationsByProduct);
}

async function loadPesticideIndicationsForProductIds(productIds: string[]): Promise<void> {
  const missingProductIds = productIds.filter(
    (productId) => (store.state as RootState).pesticideIndications.data[productId] === undefined,
  );
  if (missingProductIds.length < 1) {
    return;
  }

  const productIdsToLoad = missingProductIds.filter(
    (productId) => !pesticideIndicationsLoadingPromisesByProductId.has(productId),
  );
  if (productIdsToLoad.length > 0) {
    const loadingPromise = Promise.resolve(store.dispatch('pesticideIndications/loadByIds', productIdsToLoad))
      .then(() => undefined)
      .finally(() => {
        productIdsToLoad.forEach((productId) => {
          pesticideIndicationsLoadingPromisesByProductId.delete(productId);
        });
      });

    productIdsToLoad.forEach((productId) => {
      pesticideIndicationsLoadingPromisesByProductId.set(productId, loadingPromise);
    });
  }

  const loadingPromises = missingProductIds
    .map((productId) => pesticideIndicationsLoadingPromisesByProductId.get(productId))
    .filter((promise): promise is Promise<void> => promise !== undefined);
  await Promise.all(loadingPromises);
}

function getBbchStagesForVisualRow(
  visualRow: number,
  hot: Handsontable,
  selectedPesticideIndications: ReturnType<typeof getSelectedPesticideIndicationsForVisualRow> = [],
): BbchDropdownStage[] | null {
  const bbchStages = getCachedBbchStages();
  if (!bbchStages || bbchStages.length < 1) {
    return null;
  }

  return filterBbchStages(bbchStages, getBbchGroupIdsForVisualRow(visualRow, hot), selectedPesticideIndications)
    .map(toBbchDropdownStage)
    .filter((stage): stage is BbchDropdownStage => stage !== null);
}

function toDropdownItem(stage: BbchDropdownStage): DropdownItem {
  const bbchCode = formatBbchCode(stage.bbchCode);
  return {
    id: bbchCode,
    name: `${bbchCode} - ${stage.description}`,
  };
}

function toDropdownItems(bbchStages: BbchDropdownStage[]): DropdownItemsPerCategory[] {
  const groupedItems = bbchStages.reduce((result, stage) => {
    const current = result.get(stage.macroStage);
    if (current) {
      current.items.push(toDropdownItem(stage));
      return result;
    }

    result.set(stage.macroStage, {
      id: `bbch-${stage.macroStage}`,
      name: formatBbchMacroStageHeader(stage.macroStage, stage.macroStageName),
      items: [toDropdownItem(stage)],
      sort: false,
    });

    return result;
  }, new Map<number, DropdownItemsPerCategory>());

  return [...groupedItems.entries()].sort((a, b) => a[0] - b[0]).map(([, category]) => category);
}

function getDisplayValue(value: DropdownItem | null): string | null {
  if (!value?.id) {
    return null;
  }

  const bbchCode = sanitizeBbchCode(value.id);
  if (bbchCode === null) {
    return null;
  }

  return formatBbchCode(bbchCode);
}

function toBbchTooltipLabel(stage: BbchDropdownStage): string {
  return `${stage.macroStageName}: ${stage.description}`;
}

function triggerBbchStagesLoadingForTooltip(hot: Handsontable): void {
  const bbchStages = getCachedBbchStages();
  if (bbchStages) {
    return;
  }

  if (loadBbchStagesForTooltipPromise) {
    return;
  }

  const { $api } = store as StoreWithApi;
  if (!$api) {
    return;
  }

  loadBbchStagesForTooltipPromise = loadBbchStages($api)
    .then(() => {
      hot.render();
    })
    .finally(() => {
      loadBbchStagesForTooltipPromise = null;
    });
}

function getTooltipLabel(value: DropdownItem | null, visualRow: number, hot: Handsontable): string | null {
  if (!value?.id) {
    return null;
  }

  const bbchCode = sanitizeBbchCode(value.id);
  if (bbchCode === null) {
    return null;
  }

  if (!getBbchGroupIdForVisualRow(visualRow, hot)) {
    return null;
  }

  const stagesForVisualRow = getBbchStagesForVisualRow(visualRow, hot);
  if (!stagesForVisualRow) {
    triggerBbchStagesLoadingForTooltip(hot);
    return null;
  }

  const stage = stagesForVisualRow.find((candidate) => candidate.bbchCode === bbchCode);
  if (!stage) {
    return null;
  }

  return toBbchTooltipLabel(stage);
}

async function getItems(visualRow: number, hot: Handsontable): Promise<DropdownItemsPerCategory[]> {
  const { $api } = store as StoreWithApi;
  await loadBbchStages($api);

  const productIdsForIndications = getProductIdsForPesticideIndicationFilterForVisualRow(visualRow, hot);
  await loadPesticideIndicationsForProductIds(productIdsForIndications);

  const selectedPesticideIndications = getSelectedPesticideIndicationsForVisualRow(visualRow, hot);
  const allowedBbchStages = getBbchStagesForVisualRow(visualRow, hot, selectedPesticideIndications);
  if (!allowedBbchStages) {
    return fallbackDropdownItems;
  }

  if (allowedBbchStages.length < 1) {
    return [];
  }

  return toDropdownItems(allowedBbchStages);
}

const bbch: ColumnSettingsDropdownOptional = {
  data: 'bbchDropdownItem',
  header: {
    title: () => 'BBCH',
  },
  type: 'farmdok.dropdown',
  renderer: 'farmdok.optional',
  width: 100,
  renderEmpty,
  dropdown: {
    getItems,
    getDisplayValue,
    getTooltipLabel,
  },
  columnSorting: columnSortingDropdown,
};

export default bbch;
